import React, { useEffect, useRef, useState } from "react";

export type FilterOption = { value: string; label: string; color?: string };

type Props = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: FilterOption[];
  allLabel?: string;
  searchable?: boolean;
  className?: string;
};

/**
 * Compact filter "chip" dropdown styled to match the admin/quizzes mockup:
 * a rounded button showing `Label: value ▾`, highlighted when active, with a
 * single-select menu (plus an "all" reset row). Optional search for long
 * lists. Closes on outside click / Escape.
 */
export default function FilterSelect({
  label,
  value,
  onChange,
  options,
  allLabel = "Toate",
  searchable = false,
  className = "",
}: Props) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const active = value !== "";
  const current = options.find((o) => o.value === value);
  const filtered = searchable && term
    ? options.filter((o) => o.label.toLowerCase().includes(term.toLowerCase()))
    : options;

  const pick = (v: string) => { onChange(v); setOpen(false); setTerm(""); };

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium border transition-colors ${
          active
            ? "bg-indigo-50 border-indigo-300 text-indigo-700"
            : "bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-300"
        }`}
      >
        <span className={active ? "text-indigo-400 font-semibold" : "text-slate-400 font-semibold"}>{label}</span>
        <span className="truncate max-w-[140px]">{current ? current.label : allLabel}</span>
        <svg className={`w-3 h-3 opacity-60 transition-transform ${open ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth={2.4} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div className="absolute z-30 mt-1.5 min-w-[190px] max-w-[280px] bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
          {searchable && (
            <div className="p-2 border-b border-slate-100">
              <input
                autoFocus
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                placeholder="Caută..."
                className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}
          <div className="max-h-64 overflow-auto py-1">
            <button
              type="button"
              onClick={() => pick("")}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-slate-50 ${!active ? "text-indigo-700 font-semibold" : "text-slate-600"}`}
            >
              {allLabel}
              {!active && (
                <svg className="w-4 h-4 ml-auto text-indigo-600" fill="none" stroke="currentColor" strokeWidth={2.4} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M20 6L9 17l-5-5" /></svg>
              )}
            </button>
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-sm text-slate-400 text-center">Niciun rezultat</div>
            ) : (
              filtered.map((o) => {
                const sel = o.value === value;
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => pick(o.value)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-slate-50 ${sel ? "text-indigo-700 font-semibold" : "text-slate-700"}`}
                  >
                    {o.color && <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: o.color }} />}
                    <span className="truncate">{o.label}</span>
                    {sel && (
                      <svg className="w-4 h-4 ml-auto text-indigo-600 shrink-0" fill="none" stroke="currentColor" strokeWidth={2.4} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M20 6L9 17l-5-5" /></svg>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
